// The playbook scorecard's pure readers (lane B14, brief §2.1, contracts C1/C2/C5/C6): what `scorecard.open` sends,
// read defensively and never recomputed. The offered recommendations, the lock, the average core score, the rubric band
// and the open items are the server's (Law 3); the client only lays them out. A requisition pinned to an earlier guide
// (no `page_words`) reads the same way with its own words; a field the server has not sent reads as absent, never guessed.

import type { DraftBody, RatingInput, ScorecardOpenOutput } from "@/api/tools";
import { recommendationWord, ROOTS_LABELS, type RecoTone } from "@/mc";

export type Band = "below_vp" | "vp_plus" | "legacy";
export type ScoreValue = 1 | 2 | 3 | 4 | 5;
export type PlaybookRating = ScoreValue | "na" | null;
export type ScorecardMode = "draft" | "revision" | "paper" | "readonly";

/** ROOTS order: the stepper's and the pages' order for the values a card carries. */
export const ROOTS_ORDER = ["respect", "ownership", "opportunity", "together", "service"] as const;

export interface PlaybookValuePageData {
  key: string;
  name: string;
  page_title: string | null;
  assesses: string;
  questions: string[];
  strong: string;
  weak: string;
  anchors: Record<string, string>;
  evidence_prompt: string;
}

export interface PlaybookSkillPageData {
  number: number;
  title: string;
  what_strong_looks_like: string;
  prompts: string[];
  fit_label: string | null;
}

export interface RecommendationPageData {
  page_title: string | null;
  options: { key: string; label: string; description: string | null }[];
  justification_prompt: string | null;
  handoff_prompt: string | null;
  consider_other_role_prompt: string | null;
  consider_other_role_fields: string[];
  footer: string | null;
  decision_rule_text: string | null;
  alignment_text: string | null;
  coc_question_text: string | null;
  coc_no_instruction: string | null;
  attestation_text: string | null;
}

export interface PlaybookGuide {
  page_words: { guide_title: string; values_footer: string } | null;
  footer: string | null;
  header: { title: string | null; subtitle: string | null; guide_title: string | null; target_role: string | null };
  value_pages: PlaybookValuePageData[];
  skill_pages: PlaybookSkillPageData[];
  recommendation_page: RecommendationPageData;
  rubric: { lines: string[]; scoring_summary_text: string | null; bands_note: string | null };
  score_descriptions: Record<string, string>;
  questions_note: string | null;
  not_assessed_label: string | null;
  /** The skills page's own words when the guide carries them (title, footer); else the frame's labels. */
  skill_page: { title: string | null; footer: string | null };
}

export interface RubricBand { key: string; text: string; highlight: boolean }
export interface MissingItem { id: string; text: string }

export interface PlaybookLevel {
  band: Band | null;
  /** The pinned offered set in display order (C1). */
  offered: string[];
  hireLike: string[];
  notHire: string[];
  /** The ratings meet the band's rule (the server's answer; never a client count). */
  met: boolean;
}

export interface PlaybookOpen {
  guide: PlaybookGuide | null;
  level: PlaybookLevel;
  coreAverage: string | null;
  coreValuesScored: number;
  rubricBand: RubricBand | null;
  revisionId: string | null;
  missing: MissingItem[];
}

type Obj = Record<string, unknown>;
const obj = (value: unknown): Obj => (value !== null && typeof value === "object" && !Array.isArray(value) ? (value as Obj) : {});
const str = (value: unknown): string | null => (typeof value === "string" ? value : null);
const strs = (value: unknown): string[] => (Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : []);
const strMap = (value: unknown): Record<string, string> =>
  Object.fromEntries(Object.entries(obj(value)).filter((entry): entry is [string, string] => typeof entry[1] === "string"));

function valuePageOf(raw: unknown): PlaybookValuePageData | null {
  const v = obj(raw);
  const key = str(v["key"]);
  if (!key) return null;
  return {
    key,
    name: str(v["name"]) ?? key,
    page_title: str(v["page_title"]),
    assesses: str(v["assesses"]) ?? "",
    questions: strs(v["questions"]),
    strong: str(v["strong"]) ?? "",
    weak: str(v["weak"]) ?? "",
    anchors: strMap(v["anchors"]),
    evidence_prompt: str(v["evidence_prompt"]) ?? "",
  };
}

function skillPageOf(raw: unknown): PlaybookSkillPageData | null {
  const s = obj(raw);
  const number = typeof s["number"] === "number" ? s["number"] : Number(s["number"]);
  if (!Number.isFinite(number)) return null;
  return {
    number,
    title: str(s["title"]) ?? "",
    what_strong_looks_like: str(s["what_strong_looks_like"]) ?? "",
    prompts: strs(s["prompts"]),
    fit_label: str(s["fit_label"]),
  };
}

/** The author projection's `guide` (C1), or null when the server sent none (another projection, an older server). */
export function guideOf(raw: unknown): PlaybookGuide | null {
  if (raw === null || typeof raw !== "object") return null;
  const g = obj(raw);
  const words = obj(g["page_words"]);
  const header = obj(g["header"]);
  const reco = obj(g["recommendation_page"]);
  const rubric = obj(g["rubric"]);
  return {
    page_words: str(words["guide_title"]) !== null ? { guide_title: str(words["guide_title"])!, values_footer: str(words["values_footer"]) ?? "" } : null,
    footer: str(g["footer"]),
    header: { title: str(header["title"]), subtitle: str(header["subtitle"]), guide_title: str(header["guide_title"]), target_role: str(header["target_role"]) },
    value_pages: (Array.isArray(g["value_pages"]) ? g["value_pages"] : []).map(valuePageOf).filter((v): v is PlaybookValuePageData => v !== null),
    skill_pages: (Array.isArray(g["skill_pages"]) ? g["skill_pages"] : []).map(skillPageOf).filter((s): s is PlaybookSkillPageData => s !== null),
    recommendation_page: {
      page_title: str(reco["page_title"]),
      options: (Array.isArray(reco["options"]) ? reco["options"] : []).flatMap((o) => {
        const option = obj(o);
        const key = str(option["key"]);
        return key ? [{ key, label: str(option["label"]) ?? key, description: str(option["description"]) }] : [];
      }),
      justification_prompt: str(reco["justification_prompt"]),
      handoff_prompt: str(reco["handoff_prompt"]),
      consider_other_role_prompt: str(reco["consider_other_role_prompt"]),
      consider_other_role_fields: strs(reco["consider_other_role_fields"]),
      footer: str(reco["footer"]),
      decision_rule_text: str(reco["decision_rule_text"]),
      alignment_text: str(reco["alignment_text"]),
      coc_question_text: str(reco["coc_question_text"]),
      coc_no_instruction: str(reco["coc_no_instruction"]),
      attestation_text: str(reco["attestation_text"]),
    },
    rubric: { lines: strs(rubric["lines"]), scoring_summary_text: str(rubric["scoring_summary_text"]), bands_note: str(rubric["bands_note"]) },
    score_descriptions: strMap(g["score_descriptions"]),
    questions_note: str(g["questions_note"]),
    not_assessed_label: str(g["not_assessed_label"]),
    skill_page: { title: str(obj(g["skill_page"])["title"]), footer: str(obj(g["skill_page"])["footer"]) },
  };
}

/**
 * The same guide read from `packet.open`'s pinned `pages` (packet.render_pages) for a projection whose `scorecard.open`
 * carries no `guide` (the Recruiter compiling or keying a paper card): the rubric lines are the pinned bands' text, the
 * header's Title the requisition's, and only this card's values and skills are kept.
 */
export function guideFromPages(pages: unknown, targetRole: string | null, dims: readonly { type: string; key: string }[]): PlaybookGuide | null {
  if (pages === null || typeof pages !== "object") return null;
  const p = obj(pages);
  const reco = obj(p["recommendation_page"]);
  const values = new Set(dims.filter((d) => d.type === "value").map((d) => d.key));
  const skills = new Set(dims.filter((d) => d.type === "skill").map((d) => d.key));
  const bands = Array.isArray(reco["bands"]) ? reco["bands"] : [];
  return guideOf({
    ...p,
    header: { ...obj(p["header"]), target_role: targetRole },
    value_pages: (Array.isArray(p["value_pages"]) ? p["value_pages"] : []).filter((v) => values.has(String(obj(v)["key"]))),
    skill_pages: (Array.isArray(p["skill_pages"]) ? p["skill_pages"] : []).filter((s) => skills.has(String(obj(s)["number"]))),
    rubric: {
      lines: bands.map((b) => str(obj(b)["text"])).filter((t): t is string => t !== null),
      scoring_summary_text: str(reco["scoring_summary_text"]),
      bands_note: str(reco["bands_note"]),
    },
  });
}

/** The offered set a projection without a level block reads from the guide's own options (paper records). */
export function offeredFromGuide(guide: PlaybookGuide | null): string[] {
  return (guide?.recommendation_page.options ?? []).map((option) => option.key).filter((key) => recommendationWord(key) !== null);
}

/**
 * The level block (C1): band, the offered set in the server's order (filtered to C5's known values, never re-ordered), its
 * hire-like and not-hire sets, and whether the ratings met the band's rule — `level.met` when sent, else the author's
 * own flags, else the draft preview's `two_2s.fired` (the server's own evaluation of the pinned rule).
 */
export function levelOf(card: Obj): PlaybookLevel {
  const level = obj(card["level"]);
  const rawBand = level["band"];
  const band: Band | null = rawBand === "below_vp" || rawBand === "vp_plus" || rawBand === "legacy" ? rawBand : null;
  const offered = strs(level["offered"]).filter((value) => recommendationWord(value) !== null);
  const flags = level["own_flags"];
  const two = obj(card["two_2s"]);
  const met = typeof level["met"] === "boolean"
    ? level["met"]
    : (Array.isArray(flags) && flags.length > 0) || flags === true || two["fired"] === true || two["triggered"] === true;
  return {
    band,
    offered: offered.length ? offered : ["hire", "no_hire"],
    hireLike: strs(level["hire_like"]).length ? strs(level["hire_like"]) : ["hire"],
    notHire: strs(level["not_hire"]).length ? strs(level["not_hire"]) : ["no_hire"],
    met,
  };
}

/** The server's open items (C1 `missing[{id, text}]`); a bare string (a pre-B14 server) keeps its words with no field. */
export function missingOf(raw: unknown): MissingItem[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item): MissingItem[] => {
    if (typeof item === "string") return [{ id: "", text: item }];
    const row = obj(item);
    const text = str(row["text"]);
    return text !== null ? [{ id: str(row["id"]) ?? "", text }] : [];
  });
}

function bandOf(raw: unknown): RubricBand | null {
  const band = obj(raw);
  const key = str(band["key"]);
  const text = str(band["text"]);
  return key !== null && text !== null ? { key, text, highlight: band["highlight"] === true } : null;
}

/** The C1 fields a `scorecard.save_draft` result carries, laid over the open card so the frame reads the latest. */
export function withSaved<T extends object>(card: T, saved: Record<string, unknown> | null): T {
  if (!saved) return card;
  const keys = ["core_average", "core_values_scored", "rubric_band", "missing"] as const;
  return { ...card, ...Object.fromEntries(keys.filter((key) => key in saved).map((key) => [key, saved[key]])) };
}

/** C1 read in one place. */
export function playbookOf(card: ScorecardOpenOutput | Obj): PlaybookOpen {
  const raw = card as Obj;
  return {
    guide: guideOf(raw["guide"]),
    level: levelOf(raw),
    coreAverage: str(raw["core_average"]),
    coreValuesScored: typeof raw["core_values_scored"] === "number" ? raw["core_values_scored"] : 0,
    rubricBand: bandOf(raw["rubric_band"]),
    revisionId: str(raw["revision_id"]),
    missing: missingOf(raw["missing"]),
  };
}

// ---- dimensions -----------------------------------------------------------------------------------------------------

export interface Dimension { type: "value" | "skill"; key: string }

export const dimKey = (d: Dimension) => `${d.type}:${d.key}`;

export function dimensionsOf(card: Pick<ScorecardOpenOutput, "dimension_set">): Dimension[] {
  return card.dimension_set.flatMap((d) => {
    const type = d["type"];
    const key = d["key"];
    return (type === "value" || type === "skill") && (typeof key === "string" || typeof key === "number") ? [{ type, key: String(key) }] : [];
  });
}

const ROOTS_WORD: Readonly<Record<string, string>> = {
  respect: ROOTS_LABELS.respect, ownership: ROOTS_LABELS.ownership, opportunity: ROOTS_LABELS.opportunity,
  together: ROOTS_LABELS.together, service: ROOTS_LABELS.service,
};

/** A ROOTS value by his word; a skill by its intake title (DC-246: never a number), or null when no title is known. */
export function dimensionLabel(d: Dimension, skillTitles: Readonly<Record<string, string>> = {}): string | null {
  if (d.type === "value") return ROOTS_WORD[d.key] ?? d.key;
  return skillTitles[d.key] || null;
}

/** The skill titles a guide carries, by skill number. */
export function skillTitlesOf(guide: PlaybookGuide | null): Record<string, string> {
  return Object.fromEntries((guide?.skill_pages ?? []).filter((s) => s.title).map((s) => [String(s.number), s.title]));
}

/** The card's values in ROOTS order, then its skills in their order. */
export function orderedDimensions(dims: readonly Dimension[]): Dimension[] {
  const rank = (d: Dimension) => (d.type === "value" ? ROOTS_ORDER.indexOf(d.key as (typeof ROOTS_ORDER)[number]) : 100);
  return [...dims].sort((a, b) => {
    const ra = rank(a), rb = rank(b);
    if (ra !== rb) return (ra < 0 ? 50 : ra) - (rb < 0 ? 50 : rb);
    return a.type === "skill" && b.type === "skill" ? Number(a.key) - Number(b.key) : 0;
  });
}

export interface Entry { rating: PlaybookRating; evidence: string }

function ratingOf(row: Obj): PlaybookRating {
  if (row["state"] === "not_assessed") return "na";
  const score = row["score"];
  return score === 1 || score === 2 || score === 3 || score === 4 || score === 5 ? score : null;
}

/** The entries a stored draft or submitted revision holds, by dimension. */
export function entriesOf(rows: readonly Obj[] | null | undefined): Record<string, Entry> {
  const out: Record<string, Entry> = {};
  for (const row of rows ?? []) {
    const type = row["dimension_type"];
    const key = row["dimension_key"];
    if ((type !== "value" && type !== "skill") || (typeof key !== "string" && typeof key !== "number")) continue;
    out[`${type}:${String(key)}`] = { rating: ratingOf(row), evidence: typeof row["evidence_text"] === "string" ? row["evidence_text"] : "" };
  }
  return out;
}

// ---- the draft ------------------------------------------------------------------------------------------------------

export type Draft = Required<Pick<DraftBody, "ratings">> & Omit<DraftBody, "ratings">;

/** An empty draft for the card's dimensions: every rating unanswered, nothing chosen, not attested. */
export function emptyDraft(dims: readonly Dimension[]): Draft {
  return { ratings: dims.map((d) => ({ dimension_type: d.type, dimension_key: d.key, state: "unanswered" })), coc_answer: null, attested: false };
}

export function ratingRow(draft: Pick<Draft, "ratings">, d: Dimension): RatingInput | undefined {
  return draft.ratings.find((row) => row.dimension_type === d.type && row.dimension_key === d.key);
}

/** A stored draft (or revision) laid over the card's dimensions; API row order is never meaningful. */
export function draftOf(dims: readonly Dimension[], stored: Obj | null | undefined): Draft {
  const base = emptyDraft(dims);
  if (!stored) return base;
  const rows = Array.isArray(stored["ratings"]) ? (stored["ratings"] as RatingInput[]) : [];
  return {
    ...base,
    ...(stored as Partial<Draft>),
    ratings: base.ratings.map((row) => rows.find((r) => r.dimension_type === row.dimension_type && String(r.dimension_key) === row.dimension_key) ?? row),
  };
}

/** The draft a submitted revision reads as (the after-submission pages), its follow-up block flattened. */
export function revisionDraft(dims: readonly Dimension[], revision: Obj | null | undefined, ratings: readonly Obj[] | null | undefined): Draft {
  const rev = revision ?? {};
  const other = obj(rev["consider_other_role"]);
  const fields = ["recommendation", "reservation_text", "justification", "manager_handoff", "coc_answer", "coc_concern_text", "coc_concern_about", "private_notes"] as const;
  const picked = Object.fromEntries(fields.map((key) => [key, rev[key] ?? null]));
  return draftOf(dims, {
    ...picked,
    consider_other_role_answer: (str(other["answer"]) ?? str(rev["consider_other_role_answer"])) as Draft["consider_other_role_answer"],
    consider_other_role_suggested: str(other["suggested_role"]) ?? str(rev["consider_other_role_suggested"]),
    consider_other_role_why: str(other["why"]) ?? str(rev["consider_other_role_why"]),
    ratings: ratings ?? (Array.isArray(rev["ratings"]) ? rev["ratings"] : []),
    attested: true,
  });
}

/** One rating's next row: a score clears an Unassessed reason; Unassessed keeps the written fields (always visible). */
export function setRating(row: RatingInput, value: PlaybookRating): RatingInput {
  if (value === null) return { ...row, state: "unanswered", score: null };
  if (value === "na") return { ...row, state: "not_assessed", score: null };
  return { ...row, state: "scored", score: value, not_assessed_reason: null };
}

export const ratingValueOf = (row: RatingInput | undefined): PlaybookRating =>
  !row ? null : row.state === "not_assessed" ? "na" : row.state === "scored" && row.score != null ? (row.score as ScoreValue) : null;

/** The full body Submit sends (brief §2.1): the draft as it is, `attested` true in paper mode (the Recruiter keys a
 *  signed sheet). The recommendation is never rewritten here: a rule-bound No Hire is the server's to store (C2). */
export function submitBody(draft: Draft, mode: ScorecardMode): DraftBody {
  return mode === "paper" ? { ...draft, attested: true } : { ...draft };
}

// ---- the recommendation ---------------------------------------------------------------------------------------------

/** The rule binds (VP+ or legacy) and the ratings met it: every option but No Hire is blocked (§2.1 Lock). */
export const lockedNoHire = (level: PlaybookLevel): boolean => (level.band === "vp_plus" || level.band === "legacy") && level.met;

/** Rule-forced (C2): the rule binds while the author's own choice is not a not-hire one; the follow-up is then owed. */
export function ruleForced(level: PlaybookLevel, recommendation: string | null | undefined): boolean {
  return lockedNoHire(level) && !level.notHire.includes(recommendation ?? "");
}

/** The follow-up "consider for another position" is asked: rule-forced, or on a requisition whose No Hire always asks
 *  it (legacy / level v1: Consider Other Role is not a primary option there) when No Hire is chosen. */
export function asksFollowUp(level: PlaybookLevel, recommendation: string | null | undefined): boolean {
  if (ruleForced(level, recommendation)) return true;
  return recommendation === "no_hire" && !level.offered.includes("consider_other_role");
}

/** The value drawn as chosen: No Hire while the rule binds, else the author's own. */
export function shownRecommendation(level: PlaybookLevel, recommendation: string | null | undefined): string | null {
  return lockedNoHire(level) ? "no_hire" : recommendation ?? null;
}

export interface RecoRow { value: string; label: string; description?: string | undefined; tone: RecoTone; blocked?: { reason: string } | undefined }

/**
 * The tinted rows (C6 RecoTiles form="playbook"): exactly `level.offered`, in its order; words and tones from C5, the
 * guide's one-line description beside them. Under a binding rule every row but No Hire is blocked with the rule sentence.
 */
export function recoRows(level: PlaybookLevel, guide: PlaybookGuide | null, lockReason: string): RecoRow[] {
  const locked = lockedNoHire(level);
  return level.offered.flatMap((value): RecoRow[] => {
    const word = recommendationWord(value);
    if (!word) return [];
    const description = guide?.recommendation_page.options.find((option) => option.key === value)?.description ?? undefined;
    return [{ value, label: word.label, description: description ?? undefined, tone: word.tone, blocked: locked && value !== "no_hire" ? { reason: lockReason } : undefined }];
  });
}

// ---- pages, the stepper and the Gate --------------------------------------------------------------------------------

export type PageKey = string; // "value:<key>" | "skills" | "recommendation"

export interface PageDef { key: PageKey; anchor: string; label: string }

export const pageAnchor = (key: PageKey) => `pb-page-${key.replace(/[^a-z0-9_-]/gi, "-")}`;

/** The playbook's pages in order: each assigned value (ROOTS order), Skills when the card has skills, Recommendation. */
export function pagesOf(dims: readonly Dimension[], words: { skills: string; recommendation: string }): PageDef[] {
  const values = orderedDimensions(dims).filter((d) => d.type === "value");
  const pages: PageDef[] = values.map((d) => ({ key: `value:${d.key}`, anchor: pageAnchor(`value:${d.key}`), label: dimensionLabel(d) ?? d.key }));
  if (dims.some((d) => d.type === "skill")) pages.push({ key: "skills", anchor: pageAnchor("skills"), label: words.skills });
  pages.push({ key: "recommendation", anchor: pageAnchor("recommendation"), label: words.recommendation });
  return pages;
}

/** The field id a C1 open item names for a dimension (`rating-value-respect`). */
export const fieldId = (kind: "rating" | "evidence" | "not_assessed_reason" | "observations", d: Dimension) => `${kind}-${d.type}-${d.key}`;

/** The page an open item belongs to, by the field id it names (C1); an item without one sits on Recommendation. */
export function pageOfMissing(id: string): PageKey {
  const dim = /^(?:rating|evidence|not_assessed_reason|observations)-(value|skill)-(.+)$/.exec(id);
  if (!dim) return "recommendation";
  return dim[1] === "value" ? `value:${dim[2]}` : "skills";
}

/** The open items ordered by page (§2.1 Gate), each keeping the server's words and field. */
export function gateItems(pages: readonly PageDef[], missing: readonly MissingItem[]): (MissingItem & { page: PageKey })[] {
  const order = new Map(pages.map((page, at) => [page.key, at]));
  return missing
    .map((item, at) => ({ ...item, page: pageOfMissing(item.id), at }))
    .sort((a, b) => (order.get(a.page) ?? 99) - (order.get(b.page) ?? 99) || a.at - b.at)
    .map(({ at: _at, ...item }) => item);
}

/** A page is complete when no open item names a field on it. */
export function completedPages(pages: readonly PageDef[], missing: readonly MissingItem[]): Set<PageKey> {
  const open = new Set(missing.map((item) => pageOfMissing(item.id)));
  return new Set(pages.filter((page) => !open.has(page.key)).map((page) => page.key));
}
